import { PaymentMethodType, type PaymentMethod } from '@hooks-api/types/dataModel/payment'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Radio } from '@pets-ui/Radio/Radio'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { typography } from '@pets-ui/tokens/typography.stylex'
import { useXStyles, type XStyles } from '@pets-ui/useXStyles/useXStyles'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type PropsWithChildren, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const _baseStyles = stylex.create({
  root: {
    padding: 16,
    borderColor: colors.grey300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    gap: 8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
  },
  checked: {
    borderColor: colors.grey500,
    backgroundColor: colors.grey50,
  },
  selectWrapper: {
    maxWidth: {
      [MOBILE]: '100%',
      default: 264,
    },
  },
  descriptionHtml: {
    color: colors.grey600,
    fontSize: typography.sizeCaption,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightCaption,
  },
})

export interface RadioPaymentProps {
  rootStyle?: stylex.StyleXStyles
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  title: ReactNode
  name?: string
  checked?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
  description?: ReactNode
  paymentMethods?: PaymentMethod[]
  selectedPaymentMethodId?: number
  selectedInstallment?: number | null
  installmentOptions?: number[]
  isDisabled?: boolean
  onPaymentMethodChange?: (paymentMethod: PaymentMethod) => void
  onInstallmentChange?: (installment: number | null) => void
  renderPaymentModuleError?: () => ReactNode
  hideProviderSelect?: boolean
}

export function sanitizeToLineBreaksOnly(html: string): string {
  return html
    .replace(/<\/p>/gi, '<br>')
    .replace(/<[^>]*>/g, tag => (/^<br\s*\/?>$/i.test(tag) ? tag : ''))
    .replace(/(<br\s*\/?>)+$/gi, '')
}

export const RadioPayment = (props: PropsWithChildren<RadioPaymentProps>) => {
  const {
    rootStyle,
    xStyles,
    startIcon,
    endIcon,
    name,
    title,
    description,
    checked,
    children,
    paymentMethods = [],
    selectedPaymentMethodId,
    selectedInstallment,
    installmentOptions = [],
    isDisabled,
    onPaymentMethodChange,
    onInstallmentChange,
    renderPaymentModuleError,
    hideProviderSelect,
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const handlePaymentMethodChange = (paymentMethodId: PaymentMethod['id']) => {
    const paymentMethod = paymentMethods.find(method => method.id === paymentMethodId)
    if (!paymentMethod) return
    onPaymentMethodChange?.(paymentMethod)
  }

  return (
    <Box component="label" rootStyle={[baseStyles.root, rootStyle, checked && baseStyles.checked]} data-testid={`radio-payment-${paymentMethods[0]?.type}`}>
      <HStack gap={2} alignItems="center" flexWrap="nowrap">
        <VStack flexGrow={1} gap={2.5}>
          <HStack alignItems="center" gap={2}>
            <Radio
              disabled={isDisabled}
              name={name}
              checked={checked}
              value={selectedPaymentMethodId}
              onChange={e => handlePaymentMethodChange(Number(e.target.value))}
              color="blue"
            />
            {startIcon}
            <Text fontWeight={600} lineHeight="100%">{title}</Text>
            <Box flexGrow={1} />
            {endIcon}
          </HStack>
          <>{renderPaymentModuleError?.()}</>
          {!hideProviderSelect && paymentMethods.length > 1
          && (
            <Box rootStyle={baseStyles.selectWrapper}>
              <Select
                onChange={handlePaymentMethodChange}
                fullWidth
                value={selectedPaymentMethodId}
                disabled={!checked}
              >
                {paymentMethods.map(method => (
                  <SelectOption key={method.id} value={method.id}>{method.name}</SelectOption>
                ))}
              </Select>
            </Box>
          )}
          {
            paymentMethods[0]?.type === PaymentMethodType.CREDIT_CARD_INSTALLMENT && (
              <Box rootStyle={baseStyles.selectWrapper}>
                <Select
                  onChange={onInstallmentChange}
                  value={selectedInstallment}
                  fullWidth
                  disabled={!checked}
                >
                  {installmentOptions.map(installment => (
                    <SelectOption
                      key={installment}
                      value={installment}
                    >
                      {t('cartUi.installment', { count: installment, defaultValue: `${installment} 期0利率` })}
                    </SelectOption>
                  ))}
                </Select>
              </Box>
            )
          }
          {description && (
            typeof description === 'string'
              ? <div {...stylex.props(baseStyles.descriptionHtml)} dangerouslySetInnerHTML={{ __html: sanitizeToLineBreaksOnly(description) }} />
              : <Text variant="caption" color={colors.grey600}>{description}</Text>
          )}
        </VStack>
      </HStack>
      {children}
    </Box>
  )
}
